/**
 * The theme vocabulary, shared by the console (which applies a theme) and the
 * config surface (which offers the picker). Pure: no DOM, no matchMedia. The
 * console's src/lib/theme.ts owns everything that touches a document.
 *
 * Two vocabularies, deliberately distinct:
 *
 *   ThemeChoice  — what a person picked, six values, one of which defers to
 *                  the operating system.
 *   AppliedTheme — what is actually painted, four values, matching the
 *                  data-theme selectors in apps/console/src/styles/tokens.css.
 *
 * A choice is stored; an applied theme is derived. Collapsing them would
 * make "follow my system" unstorable.
 */
/**
 * The six picker entries, in the order they appear in the dropdown. The
 * string IS the stored value: `kind: 'select'` fields render each option with
 * value and label identical, so a stable id and a readable label are the same
 * thing here. That means these strings must not be reworded casually — a
 * rename orphans everyone's stored preference.
 */
export const THEME_CHOICES = [
    'Tossker Auto',
    'Tossker Day',
    'Tossker Night',
    'Legacy Auto',
    'Legacy Day',
    'Legacy Night',
];
/**
 * Legacy Night — exactly what this console looked like before themes existed.
 * Phase 1 ships the machinery with no visual change; the default flips to a
 * Tossker theme when the repaint lands, not before.
 */
export const THEME_CHOICE_DEFAULT = 'Legacy Night';
/** The four values data-theme can hold. Must match tokens.css's selectors exactly. */
export const APPLIED_THEMES = ['tossker-day', 'tossker-night', 'legacy-day', 'legacy-night'];
export function isAppliedTheme(value) {
    return typeof value === 'string' && APPLIED_THEMES.includes(value);
}
/**
 * Where the console caches the last resolved theme so the first paint is
 * already right. Declared here rather than in the console because TWO
 * separate things read it and they must not drift: `src/lib/theme-cache.ts`
 * at runtime, and the inline `<head>` script that Vite generates at build
 * time (see `apps/console/vite.config.ts`). That script is plain text in the
 * HTML document and cannot import anything, so it is generated from this
 * constant rather than repeating it.
 */
export const THEME_STORAGE_KEY = 'communicator.theme';
export function isThemeChoice(value) {
    return typeof value === 'string' && THEME_CHOICES.includes(value);
}
/**
 * Which of the four themes to paint. `prefersDark` is only consulted for the
 * two Auto choices; an explicit Day or Night always wins, because a person
 * who picked one asked for it on every machine they sign in from.
 */
export function resolveTheme(choice, prefersDark) {
    switch (choice) {
        case 'Tossker Day':
            return 'tossker-day';
        case 'Tossker Night':
            return 'tossker-night';
        case 'Legacy Day':
            return 'legacy-day';
        case 'Legacy Night':
            return 'legacy-night';
        case 'Tossker Auto':
            return prefersDark ? 'tossker-night' : 'tossker-day';
        case 'Legacy Auto':
            return prefersDark ? 'legacy-night' : 'legacy-day';
    }
}
